import type { Weight } from '@iheartjane/types';
import { formatCurrency } from '@iheartjane/utils';
import type { SortingState } from '@tanstack/react-table';
import capitalize from 'lodash-es/capitalize';
import compact from 'lodash-es/compact';
import findKey from 'lodash-es/findKey';
import flatten from 'lodash-es/flatten';
import get from 'lodash-es/get';
import isEmpty from 'lodash-es/isEmpty';
import isEqual from 'lodash-es/isEqual';
import keys from 'lodash-es/keys';
import pickBy from 'lodash-es/pickBy';
import set from 'lodash-es/set';

import type {
  AbbreviatedSpecialV2,
  GlobalSpecialV2,
  SpecialRulesV2,
  StoreSpecialV2,
} from '@jane/business-admin/types';
/* eslint-disable-next-line no-restricted-imports */
import type {
  AdditionalProductWeight,
  BrandCondition,
  BulkWeightCondition,
  KindCondition,
  ProductConditions,
  ProductThresholdConditions,
  SpecialConditions,
  WeightCondition,
  WeightValueCondition,
} from '@jane/shared/models';
import { formatTime, titleCase, titleize } from '@jane/shared/util';

import { ALL_WEIGHTS } from './weights';

export const MAX_MESSAGE_LENGTH = 300;
export const FORM_FIELD_DEFAULT_MARGIN = 24;

export enum SPECIAL_TYPES {
  BulkPricing = 'bulk_pricing',
  Bundle = 'bundle',
  CartTotal = 'cart_total',
  Product = 'product',
  QualifiedGroup = 'qualified_group',
  SpendingThreshold = 'spending_threshold',
}

export const ADDITIONAL_WEIGHTS: (
  | Weight
  | { id: AdditionalProductWeight; label: string; val: string }
)[] = [
  { id: 'half_gram', label: '0.5g', val: 'price_half_gram' },
  { id: 'gram', label: '1g', val: 'price_gram' },
  { id: 'two_gram', label: '2g', val: 'price_two_gram' },
  { id: 'three_gram', label: '3g', val: 'price_three_gram' },
  { id: 'eighth_ounce', label: '3.5g', val: 'price_eighth_ounce' },
  { id: 'four_gram', label: '4g', val: 'price_four_gram' },
  { id: 'five_gram', label: '5g', val: 'price_five_gram' },
  { id: 'quarter_ounce', label: '7g', val: 'price_quarter_ounce' },
  { id: 'eight_gram', label: '8g', val: 'price_eight_gram' },
  { id: 'ten_gram', label: '10g', val: 'price_ten_gram' },
  { id: 'half_ounce', label: '14g', val: 'price_half_ounce' },
  { id: 'ounce', label: '28g', val: 'price_ounce' },
  { id: 'two_ounce', label: '56g', val: 'price_two_ounce' },
  { id: 'two_and_half_ounce', label: '70g', val: 'price_two_and_half_ounce' },
];

const WEIGHT_OPTIONS = [
  { label: '1/2 G', value: 'half_gram' },
  { label: '1 G', value: 'gram' },
  { label: '2 G', value: 'two_gram' },
  { label: '1/8 OZ', value: 'eighth_ounce' },
  { label: '1/4 OZ', value: 'quarter_ounce' },
  { label: '1/2 OZ', value: 'half_ounce' },
  { label: '1 OZ', value: 'ounce' },
];

const WEEKEND_DAYS = ['saturday', 'sunday'];

const WEEKDAYS = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday'];

export const shouldShowArchiveButton = ({
  isArchived,
  isConnectedToGlobal,
  isEnabled,
  isPosSynced,
}: {
  isArchived?: boolean;
  isConnectedToGlobal: boolean;
  isEnabled?: boolean;
  isPosSynced?: boolean;
}) => {
  if (isArchived || isConnectedToGlobal) {
    return false;
  }
  if (isPosSynced && isEnabled) {
    return false;
  }
  return true;
};

export const getDiscountAmount = (row: AbbreviatedSpecialV2) => {
  let isPercent = false;
  let amount = 0;

  switch (row.discount_type) {
    case 'percent':
      amount = row.discount_percent || 0;
      isPercent = true;
      break;
    case 'dollar_amount':
      amount = row.discount_dollar_amount || 0;
      break;
    case 'target_price':
      amount = row.discount_target_price || 0;
      break;
  }

  if (isPercent) {
    return `${amount}%`;
  }

  const formatter = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
  });

  return formatter.format(amount);
};

export const specialsSortParams = (
  sorting: SortingState,
  statusFilter: string
) => {
  const isSorted = Array.isArray(sorting) && sorting.length > 0;

  if (!isSorted) return {};

  if (statusFilter !== 'archived' && sorting[0]?.id === 'archived_at') {
    return {};
  }

  const sort = sorting[0].id;
  const order = sorting[0].desc ? 'desc' : 'asc';

  return { sort, order };
};

export const buildRules = ({
  excludeBrand = false,
  excludeBrandId = false,
  excludeBulkWeights = false,
  excludeCategory = false,
  excludeProduct = false,
  excludeWeight = false,
  excludeWeightValue = false,
  includeBrand = false,
  includeBrandId = false,
  includeBulkWeights = false,
  includeCategory = false,
  includeLineage = false,
  includePrices = false,
  includeProduct = false,
  includeWeight = false,
  includeWeightValue = false,
}: {
  excludeBrand?: boolean;
  excludeBrandId?: boolean;
  excludeBulkWeights?: boolean | any[];
  excludeCategory?: boolean;
  excludeProduct?: boolean | any;
  excludeWeight?: boolean | any[];
  excludeWeightValue?: boolean | any;
  includeBrand?: boolean | any;
  includeBrandId?: boolean | any;
  includeBulkWeights?: boolean | any[];
  includeCategory?: boolean | any;
  includeLineage?: boolean | any;
  includePrices?: boolean | any;
  includeProduct?: boolean | any;
  includeWeight?: boolean | any[];
  includeWeightValue?: boolean | any[];
}) => {
  const rules: SpecialRulesV2 = {};
  // TODO: remove once we stop persisting brand name conditions
  if (excludeBrand) {
    rules['excludes'] = [
      {
        brands:
          typeof excludeBrand === 'boolean' ? ['Test Brand'] : excludeBrand,
      },
    ];
  }
  if (excludeBrandId) {
    rules['excludes'] = [
      {
        brand_ids: typeof excludeBrandId === 'boolean' ? [420] : excludeBrandId,
      },
    ];
  }
  if (excludeCategory) {
    if (rules['excludes']) {
      rules['excludes'][0]['kinds'] = [{ kind: 'flower' }];
    } else {
      rules['excludes'] = [
        {
          kinds: [{ kind: 'flower' }],
        },
      ];
    }
  }
  if (excludeProduct) {
    const productRule =
      typeof excludeProduct === 'boolean' ? [1] : excludeProduct;
    set(rules, 'excludes.0.product_ids', productRule);
  }
  if (excludeWeight) {
    const weightRule =
      typeof excludeWeight === 'boolean' ? ['half_gram'] : excludeWeight;
    set(rules, 'excludes.0.weights', weightRule);
  }
  if (excludeWeightValue) {
    const weightRule =
      typeof excludeWeightValue === 'boolean'
        ? [{ units: 'g', value: 0.5 }]
        : excludeWeightValue;
    set(rules, 'excludes.0.weight_values', weightRule);
  }
  // TODO: remove once we stop persisting brand name conditions
  if (includeBrand) {
    const brandRule =
      typeof includeBrand === 'boolean' ? ['Test Brand'] : includeBrand;
    set(rules, 'includes.0.brands', brandRule);
  }
  if (includeBrandId) {
    const brandIdRule =
      typeof includeBrandId === 'boolean' ? [420] : includeBrandId;
    set(rules, 'includes.0.brand_ids', brandIdRule);
  }
  if (includeCategory) {
    const categoryRule =
      typeof includeCategory === 'boolean'
        ? [{ kind: 'flower' }]
        : includeCategory;
    const transformedRule = transformRules(categoryRule);

    set(rules, 'includes.0.kinds', transformedRule);
  }
  if (includeLineage) {
    const lineageRule =
      typeof includeLineage === 'boolean' ? ['cbd'] : includeLineage;
    set(rules, 'includes.0.lineages', lineageRule);
  }
  if (includePrices) {
    const priceRule =
      typeof includePrices === 'boolean'
        ? {
            maximum_price: 100,
            minimum_price: 10,
          }
        : includePrices;
    rules['product_threshold'] = priceRule;
  }
  if (includeProduct) {
    const productRule =
      typeof includeProduct === 'boolean' ? [1] : includeProduct;
    set(rules, 'includes.0.product_ids', productRule);
  }
  if (includeWeight) {
    const weightRule =
      typeof includeWeight === 'boolean' ? ['half_gram'] : includeWeight;
    set(rules, 'includes.0.weights', weightRule);
  }
  if (includeBulkWeights) {
    const weightRule =
      typeof includeBulkWeights === 'boolean'
        ? [
            {
              price_id: 'half_gram',
              maximum_price: 20,
              minimum_price: 10,
            },
          ]
        : includeBulkWeights;
    set(rules, 'includes.0.bulk_weights', weightRule);
  }
  if (excludeBulkWeights) {
    const weightRule =
      typeof excludeBulkWeights === 'boolean'
        ? [{ price_id: 'half_gram' }]
        : excludeBulkWeights;
    set(rules, 'excludes.0.bulk_weights', weightRule);
  }
  if (includeWeightValue) {
    const weightRule =
      typeof includeWeightValue === 'boolean'
        ? ['half_gram']
        : includeWeightValue;
    set(rules, 'includes.0.weight_values', weightRule);
  }
  return rules;
};

/**
 * Transforms rules by ensuring that if an object has both `kind` and `brand_subtype`,
 * it also includes `root_subtype: "(Subcategory not set)"` if `root_subtype` is missing.
 * @param rules The rules object to transform.
 * @returns A transformed rules object with the missing `root_subtype` added where applicable.
 */
export const transformRules = (rules: any): any => {
  if (Array.isArray(rules)) {
    return rules.map((rule) => {
      if (
        typeof rule === 'object' &&
        rule !== null &&
        rule['kind'] &&
        !rule['root_subtype'] &&
        rule['brand_subtype']
      ) {
        // Add the missing root_subtype here
        return {
          ...rule,
          root_subtype: '(Subcategory not set)',
        };
      }
      return transformRules(rule);
    });
  } else if (typeof rules === 'object' && rules !== null) {
    return Object.entries(rules).reduce((acc, [key, value]) => {
      acc[key] = transformRules(value);
      return acc;
    }, {} as any);
  }

  return rules;
};

/**
 * Recursively removes (i.e. "sanitizes") properties where the value is "(Subcategory not set)".
 * @param rules The object to sanitize.
 * @returns A sanitized object with the unwanted properties removed.
 */
export const sanitizeRules = (rules: any): any => {
  if (Array.isArray(rules)) {
    return rules.map((item) => sanitizeRules(item));
  } else if (typeof rules === 'object' && rules !== null) {
    return Object.entries(rules).reduce((acc, [key, value]) => {
      const sanitizedValue = sanitizeRules(value);
      // Defacto way of removing (Subcategory not set) by NOT adding it to acc
      if (
        sanitizedValue !== '(Subcategory not set)' &&
        sanitizedValue !== null
      ) {
        acc[key] = sanitizedValue;
      }
      return acc;
    }, {} as any);
  }
  return rules;
};

type BundleConditions = {
  brandConditions: boolean | any;
  brandIdConditions: boolean | any;
  excludedProductConditions: boolean | any;
  includedProductConditions: boolean | any;
  kindConditions: boolean | any;
  lineageConditions: boolean | any;
  priceConditions: boolean | any;
  weightConditions: boolean | any;
};
export const buildBundleConditions = (conditions: BundleConditions) => {
  const returnedConditions: ProductConditions = {};

  const brandCondition = get(conditions, `brandConditions`);
  if (brandCondition) {
    set(returnedConditions, `brands`, brandCondition);
  }
  const brandIdCondition = get(conditions, `brandIdConditions`);
  if (brandIdCondition) {
    set(returnedConditions, `brand_ids`, brandIdCondition);
  }

  const excludedProductConditions = get(
    conditions,
    `excludedProductConditions`
  );
  if (excludedProductConditions) {
    set(returnedConditions, `excluded_product_ids`, excludedProductConditions);
  }

  const includedProductConditions = get(
    conditions,
    `includedProductConditions`
  );
  if (includedProductConditions) {
    set(returnedConditions, `included_product_ids`, includedProductConditions);
  }

  const kindConditions = get(conditions, `kindConditions`);
  if (kindConditions) {
    set(returnedConditions, `kinds`, kindConditions);
  }

  const lineageConditions = get(conditions, `lineageConditions`);
  if (lineageConditions) {
    // Emit only the plural `lineages` shape. Do NOT also write the singular
    // `lineage` — the cart engine's Bundle.applicability ANDs every condition
    // key together, so having both would restrict qualification to the single
    // mirrored value (e.g. `lineages: ["indica","sativa"]` + `lineage: "indica"`
    // would silently exclude sativa products).
    set(returnedConditions, `lineages`, lineageConditions);
  }

  const priceConditions = get(conditions, `priceConditions`);
  if (priceConditions) {
    set(returnedConditions, `product_threshold`, priceConditions);
  }

  const weightConditions = get(conditions, `weightConditions`);
  if (weightConditions) {
    set(returnedConditions, `weights`, weightConditions);
  }

  const weightValueConditions = get(conditions, `weightValueConditions`);
  if (weightValueConditions) {
    set(returnedConditions, `weight_values`, weightValueConditions);
  }

  return returnedConditions;
};

const parseDates = (special: StoreSpecialV2 | GlobalSpecialV2) => {
  const fullEndDate = special?.end_time ? special?.end_time.split(' ') : [];
  const fullStartDate = special?.start_time
    ? special?.start_time.split(' ')
    : [];
  const endDate = fullEndDate[0];
  const startDate = fullStartDate[0];

  return {
    start_date: startDate,
    end_date: endDate,
  };
};

export const getEnabledDaysDisplay = (
  special: StoreSpecialV2 | GlobalSpecialV2
) => {
  const enabled = daysEnabled(special);

  if (arrayMatch(enabled, [...WEEKDAYS, ...WEEKEND_DAYS])) return 'Everyday';
  if (arrayMatch(enabled, WEEKEND_DAYS)) return 'Weekends';
  if (arrayMatch(enabled, WEEKDAYS)) return 'Weekdays';

  return enabled.map((day) => `${capitalize(day)}s`).join(', ');
};

const formatDate = (date: string) =>
  formatTime({ time: date, outputFormat: 'M/D/YYYY' });

export const getEnabledDatesDisplay = ({
  start_date,
  end_date,
}: {
  end_date: string | null;
  start_date: string | null;
}) => {
  if (start_date && end_date)
    return `${formatDate(start_date)} - ${formatDate(end_date)}`;
  if (start_date) return `Starts: ${formatDate(start_date)}`;
  if (end_date) return `Expires: ${formatDate(end_date)}`;

  return '';
};

const enabledOnDay =
  (special: StoreSpecialV2 | GlobalSpecialV2) => (day: string) =>
    (special as any).schedule?.[`enabled_${day}`];

const arrayMatch = <T>(a: T[], b: T[]) => isEqual([...a].sort(), [...b].sort());

const daysEnabled = (special: StoreSpecialV2 | GlobalSpecialV2) =>
  [
    'sunday',
    'monday',
    'tuesday',
    'wednesday',
    'thursday',
    'friday',
    'saturday',
  ].filter(enabledOnDay(special));

const parseKindConditions = (
  kindConditions: readonly KindCondition[] | undefined
) =>
  kindConditions && kindConditions.length
    ? kindConditions.map((c) => {
        const brandSubtypeText = c.brand_subtype
          ? ` - ${titleCase(c.brand_subtype)}`
          : '';
        const lineageText = c.lineage ? ` - ${titleCase(c.lineage)}` : '';
        const rootSubtypeText = c.root_subtype
          ? ` - ${titleCase(c.root_subtype)}`
          : '';
        return `${titleCase(
          c.kind
        )}${rootSubtypeText}${brandSubtypeText}${lineageText}`;
      })
    : ['All Products'];

const parseBrandConditions = (
  brandConditions: readonly BrandCondition[] | undefined
) =>
  brandConditions && brandConditions.length ? brandConditions : ['All Brands'];

const parseWeightConditions = (
  weightConditions: readonly WeightCondition[] | undefined
) =>
  weightConditions && weightConditions.length
    ? weightConditions.map((weightCondition) => {
        const weightOption = WEIGHT_OPTIONS.find(
          (wo) => wo.value === weightCondition
        );
        return weightOption ? weightOption.label : '';
      })
    : ['All Weights'];

const parseBulkWeightConditions = (
  bulkWeightConditions?: readonly BulkWeightCondition[]
) => {
  if (!(bulkWeightConditions && bulkWeightConditions.length > 0)) {
    return ['All Weights'];
  }

  return compact(
    bulkWeightConditions.map((weightCondition) => {
      const weightOption = WEIGHT_OPTIONS.find(
        (wo) => wo.value === weightCondition.price_id
      );
      if (!weightOption) return null;

      const label = weightOption.label;
      const min = weightCondition.minimum_price;
      const max = weightCondition.maximum_price;

      if (!(min || max)) return label;
      const minMax = minMaxText(min, max);
      return `${label} ${minMax}`;
    })
  );
};

const minMaxText = (
  min: number | null | undefined,
  max: number | null | undefined
) => {
  const minText = min ? `Min: $${min}` : '';
  const maxText = max ? `Max: $${max}` : '';
  if (minText.length && maxText) return `${minText} - ${maxText}`;
  if (minText) return minText;
  return maxText;
};

const parseProductThresholdConditions = (
  thresholdConditions: ProductThresholdConditions | undefined
) => {
  if (thresholdConditions) {
    const min = thresholdConditions.minimum_price;
    const max = thresholdConditions.maximum_price;
    if (!(min || max)) {
      return [undefined];
    }
    return min && max
      ? [`Min: $${min}`, `Max: $${max}`]
      : min
        ? [`Min: $${min}`]
        : [`Max: $${max}`];
  }

  return [undefined];
};

const parseLineageConditions = (lineageCondition?: string) => {
  if (lineageCondition) {
    return [titleize(lineageCondition)];
  }
  return ['All Lineages'];
};

const reservationModesDisplay = (
  reservationModes: StoreSpecialV2 | GlobalSpecialV2['reservation_modes']
) => {
  if (!reservationModes) return ['Pickup', 'Delivery'];
  const applicableModes = pickBy(reservationModes, (value, _) => value as any);
  return keys(applicableModes).map((mode) => titleCase(mode));
};

export const getConditionDisplay = (conditions: SpecialConditions) => {
  if (conditions.cart_total)
    return [`Cart Total More Than $${conditions.cart_total.threshold}`];
  if (conditions.qualified_group)
    return [
      conditions.qualified_group.type === 'senior'
        ? `Seniors ${conditions.qualified_group.required_age} and over`
        : capitalize(conditions.qualified_group.type),
    ];
  if (conditions.product) {
    if (isEmpty(conditions.product)) return ['All Products'];

    if (
      conditions.product.included_product_ids &&
      conditions.product.included_product_ids.length
    ) {
      return [
        `Individual Products: ${conditions.product.included_product_ids.length}`,
      ].concat(parseWeightConditions(conditions.product.weights));
    }

    return parseKindConditions(conditions.product.kinds)
      .concat(
        parseBrandConditions(conditions.product.brands),
        parseWeightConditions(conditions.product.weights),
        parseProductThresholdConditions(
          conditions.product.product_threshold
        ) as string[],
        parseLineageConditions(conditions.product.lineage)
      )
      .filter(Boolean);
  }

  if (conditions.bulk_pricing) {
    if (isEmpty(conditions.bulk_pricing))
      return ['Bulk Pricing', 'All Products'];

    if (
      conditions.bulk_pricing.included_product_ids &&
      conditions.bulk_pricing.included_product_ids.length
    ) {
      return [
        'Bulk Pricing',
        `Individual Products: ${conditions.bulk_pricing.included_product_ids.length}`,
      ].concat(parseBulkWeightConditions(conditions.bulk_pricing.bulk_weights));
    }

    return parseKindConditions(conditions.bulk_pricing.kinds)
      .concat(
        parseBrandConditions(conditions.bulk_pricing.brands),
        parseBulkWeightConditions(conditions.bulk_pricing.bulk_weights),
        parseLineageConditions(conditions.bulk_pricing.lineage)
      )
      .filter(Boolean);
  }

  return null;
};

const typeAmountText = (special: StoreSpecialV2 | GlobalSpecialV2) => {
  const {
    discount_type,
    discount_dollar_amount,
    discount_percent,
    discount_target_price,
  } = special;

  if (discount_type === 'percent') {
    return `${discount_percent}% off`;
  }

  if (discount_type === 'target_price') {
    return `Target price: ${formatCurrency(discount_target_price)}`;
  }

  return `${formatCurrency(discount_dollar_amount)} off`;
};

export const detailText = ({
  special,
}: {
  special: StoreSpecialV2 | GlobalSpecialV2;
}): readonly string[] => {
  const { conditions, reservation_modes } = special;

  const conditionBadges = (conditions && getConditionDisplay(conditions)) || [];

  const reservationModeBadges = reservationModesDisplay(reservation_modes);

  const typeAmountBadge = typeAmountText(special);

  const dayBadge = getEnabledDaysDisplay(special);

  const { start_date, end_date } = parseDates(
    special as StoreSpecialV2 | GlobalSpecialV2
  );

  const dateBadge = getEnabledDatesDisplay({ start_date, end_date });

  return compact(
    flatten(
      conditionBadges.concat(
        typeAmountBadge,
        dayBadge,
        dateBadge,
        reservationModeBadges
      )
    )
  );
};

// React hook form doesn't like decimals in field names
export const removeDecimals = (string: any) => string.replace('.', '*');
export const restoreDecimals = (string: any) => string.replace('*', '.');

export const generateDefaultWeightValues = (weightKeys: string[]) => {
  const defaultWeights: { [key: string]: any } = {};
  Object.keys(ALL_WEIGHTS).forEach((key: string) => {
    defaultWeights[removeDecimals(key)] = weightKeys.includes(key);
  });
  return defaultWeights;
};

export const weightConditionsToKeys = (conditions: WeightValueCondition[]) => {
  return conditions.reduce<string[]>((prev, con) => {
    const foundKey = findKey(
      ALL_WEIGHTS,
      (weight) => weight.value == con.value && weight.units == con.units
    );
    if (foundKey) {
      prev.push(foundKey);
    }
    return prev;
  }, []);
};
